import { fixed, inches, mm, trim } from './format';
import { nearestDrill, type DrillMatch } from './drills';
import { require, requirePositive, step, type Solved } from './types';

/**
 * Constant from the 60° unified thread form. The single depth of a sharp
 * V-thread is 0.86603 × pitch; the tap-drill rule uses 1.299 = 2 × 0.6495,
 * which is the amount that comes off the *diameter* for 100% of thread.
 */
export const THREAD_CONSTANT = 1.299;

/** Inch threads: pitch is the reciprocal of threads per inch. */
export function pitchFromTpi(tpi: number): Solved<number> {
  requirePositive(tpi, 'threads per inch');
  const value = 1 / tpi;
  return {
    value,
    steps: [
      step('formula', 'pitch = 1 ÷ TPI'),
      step('substitute', `1 ÷ ${trim(tpi, 2)}`),
      step('result', `= ${inches(value)} from one crest to the next`),
      step('note', 'Pitch and TPI are the same fact stated two ways. Metric threads are called out by pitch, inch threads by TPI.'),
    ],
  };
}

export function tpiFromPitch(pitch: number): Solved<number> {
  requirePositive(pitch, 'pitch');
  const value = 1 / pitch;
  return {
    value,
    steps: [
      step('formula', 'TPI = 1 ÷ pitch'),
      step('substitute', `1 ÷ ${trim(pitch, 4)}`),
      step('result', `= ${fixed(value, 2)} threads per inch`),
    ],
  };
}

export interface TapDrill {
  /** Theoretical hole size for the requested engagement. */
  readonly size: number;
  /** Closest real drill in the rack. */
  readonly drill: DrillMatch;
  /** Engagement the real drill actually delivers, in percent. */
  readonly actualPercent: number;
}

/**
 * Tap drill for an inch thread.
 *
 * 75% engagement is the shop default: it holds essentially all the strength of
 * a full thread while cutting tapping torque roughly in half. Chasing 100% is
 * how taps get snapped off in a part.
 */
export function tapDrillInch(
  majorDiameter: number,
  tpi: number,
  percentOfThread = 75,
): Solved<TapDrill> {
  requirePositive(majorDiameter, 'major diameter');
  requirePositive(tpi, 'threads per inch');
  require(
    percentOfThread > 0 && percentOfThread <= 100,
    'Percent of thread must be between 0 and 100',
  );

  const reduction = (THREAD_CONSTANT * (percentOfThread / 100)) / tpi;
  const size = majorDiameter - reduction;
  require(size > 0, 'That combination gives a hole size of zero or less');

  const drill = nearestDrill(size);
  const actualPercent = ((majorDiameter - drill.drill.inches) * 100 * tpi) / THREAD_CONSTANT;

  return {
    value: { size, drill, actualPercent },
    steps: [
      step('formula', 'tap drill = major Ø − (1.299 × %thread ÷ 100) ÷ TPI'),
      step('compute', `1.299 × ${percentOfThread} ÷ 100 = ${fixed(THREAD_CONSTANT * (percentOfThread / 100), 4)}`),
      step('compute', `${fixed(THREAD_CONSTANT * (percentOfThread / 100), 4)} ÷ ${trim(tpi, 2)} = ${fixed(reduction, 4)}`),
      step('substitute', `${trim(majorDiameter)} − ${fixed(reduction, 4)}`),
      step('result', `= ${inches(size)}, so reach for the ${drill.drill.label} (${inches(drill.drill.inches, 4)})`),
      step(
        'note',
        `That drill gives about ${fixed(actualPercent, 0)}% of thread. The quick shop rule — major Ø minus 1÷TPI — gets you to the same place for 75%.`,
      ),
    ],
  };
}

/** Tap drill for a metric thread, where the callout is pitch rather than TPI. */
export function tapDrillMetric(
  majorDiameterMm: number,
  pitchMm: number,
  percentOfThread = 75,
): Solved<{ size: number }> {
  requirePositive(majorDiameterMm, 'major diameter');
  requirePositive(pitchMm, 'pitch');
  require(
    percentOfThread > 0 && percentOfThread <= 100,
    'Percent of thread must be between 0 and 100',
  );

  const reduction = THREAD_CONSTANT * (percentOfThread / 100) * pitchMm;
  const size = majorDiameterMm - reduction;
  require(size > 0, 'That combination gives a hole size of zero or less');

  return {
    value: { size },
    steps: [
      step('formula', 'tap drill = major Ø − (1.299 × %thread ÷ 100) × pitch'),
      step('substitute', `${trim(majorDiameterMm, 2)} − (${fixed(THREAD_CONSTANT * (percentOfThread / 100), 4)} × ${trim(pitchMm, 2)})`),
      step('result', `= ${mm(size)}`),
      step(
        'note',
        'The rough metric rule is major Ø minus the pitch — M8×1.25 wants a 6.8 mm drill, and 8 − 1.25 = 6.75. Close enough to remember it that way.',
      ),
    ],
  };
}

/** Single depth of thread — how deep the form cuts on one side. */
export function threadDepth(tpi: number): Solved<number> {
  requirePositive(tpi, 'threads per inch');
  const value = 0.6134 / tpi;
  return {
    value,
    steps: [
      step('formula', 'single depth = 0.6134 ÷ TPI'),
      step('substitute', `0.6134 ÷ ${trim(tpi, 2)}`),
      step('result', `= ${inches(value)}`),
      step(
        'note',
        'That is depth on one flank. Coming in on a diameter with a threading tool, the cross-slide travels this amount, not double it.',
      ),
    ],
  };
}

/** Percent of thread a given drill actually produces. */
export function percentOfThread(
  majorDiameter: number,
  tpi: number,
  drillSize: number,
): Solved<number> {
  requirePositive(majorDiameter, 'major diameter');
  requirePositive(tpi, 'threads per inch');
  requirePositive(drillSize, 'drill size');
  require(drillSize < majorDiameter, 'The drill must be smaller than the major diameter');

  const difference = majorDiameter - drillSize;
  const value = (difference * 100 * tpi) / THREAD_CONSTANT;

  return {
    value,
    steps: [
      step('formula', '%thread = (major Ø − drill) × 100 × TPI ÷ 1.299'),
      step('compute', `${trim(majorDiameter)} − ${trim(drillSize, 4)} = ${fixed(difference, 4)}`),
      step('substitute', `${fixed(difference, 4)} × 100 × ${trim(tpi, 2)} ÷ 1.299`),
      step('result', `= ${fixed(value, 0)}% of thread`),
    ],
  };
}
